import { Pencil } from "lucide-react";
import { eur } from "@/lib/format";

export const BPM_TONE = {
  EXACT: "text-emerald-700",
  HOGE_BETROUWBAARHEID: "text-emerald-700",
  GESCHAT: "text-slate-800",
  LAGE_BETROUWBAARHEID: "text-amber-700",
  TARIEF_ONTBREEKT: "text-amber-700",
  HANDMATIGE_CONTROLE_VEREIST: "text-red-700",
  HANDMATIG: "text-blue-800",
};

export const isManualBpm = (row) =>
  (row.headline || {}).bpm_source === "MANUAL" || (row.bpm || {}).manual_override === true;

export const bpmTooltip = (row) => {
  const h = row.headline || {};
  const auto = (row.bpm || {}).automatic_bpm ?? h.bpm_automatic;
  return [
    `Automatische BPM: ${eur(auto, { fallback: "niet te bepalen" })}`,
    isManualBpm(row) ? `Handmatige BPM: ${eur(h.bpm_manual ?? h.bpm)}` : null,
    `Gebruikte BPM: ${eur(h.bpm, { fallback: "onbekend" })}`,
  ]
    .filter(Boolean)
    .join("\n");
};

const bpmRangeText = (h) =>
  h.bpm_has_range && h.bpm_low !== h.bpm_high
    ? `${eur(h.bpm_low)} – ${eur(h.bpm_high)}`
    : eur(h.bpm);

// Mobiele BPM-tekst zonder geneste ternary.
export function bpmMobileText(row) {
  const h = row.headline || {};
  if (h.bpm === null || h.bpm === undefined) return "BPM onbekend";
  if (isManualBpm(row)) return eur(h.bpm);
  return bpmRangeText(h);
}

export const Cell = ({ children, onClick, testid, className = "" }) => {
  if (!onClick) {
    return (
      <td className={className} data-testid={testid}>
        {children}
      </td>
    );
  }
  return (
    <td
      className={`${className} cursor-pointer`}
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <span className="underline decoration-slate-300 decoration-dotted underline-offset-2 transition-colors duration-150 hover:decoration-blue-500">
        {children}
      </span>
    </td>
  );
};

// De drie BPM-weergaven (onbekend / handmatig / geschat) als early returns.
const BpmValue = ({ row }) => {
  const h = row.headline || {};
  const bpm = row.bpm || {};
  if (h.bpm === null || h.bpm === undefined) {
    return (
      <>
        <span className="text-[11px] font-semibold text-red-700">BPM ONBEKEND</span>
        <div className="text-[9.5px] uppercase tracking-wide text-red-600">CO₂ onvoldoende</div>
      </>
    );
  }
  if (isManualBpm(row)) {
    const auto = bpm.automatic_bpm ?? h.bpm_automatic;
    return (
      <>
        {eur(h.bpm)}
        <div
          className="text-[9.5px] font-bold uppercase tracking-wide text-blue-700"
          data-testid={`bpm-manual-${row.listing_id}`}
        >
          Handmatig
          {h.bpm_override_scope === "GROUP" ? " · groep" : ""}
        </div>
        {auto !== null && auto !== undefined && (
          <div className="text-[9px] text-slate-400">auto {eur(auto)}</div>
        )}
      </>
    );
  }
  return (
    <>
      {bpmRangeText(h)}
      <div className="text-[9.5px] uppercase tracking-wide text-slate-500">
        {bpm.status_label}
        {h.co2_confidence ? ` · CO₂ ${Math.round(h.co2_confidence)}%` : ""}
      </div>
    </>
  );
};

export const BpmCell = ({ row, onExplain, onEdit }) => {
  const bpm = row.bpm || {};
  return (
    <td
      className={`num ${BPM_TONE[bpm.status] || ""}`}
      data-testid={`bpm-cell-${row.listing_id}`}
      title={bpmTooltip(row)}
    >
      <div className="flex items-start justify-end gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExplain();
          }}
          className="text-right underline decoration-slate-300 decoration-dotted underline-offset-2 transition-colors duration-150 hover:decoration-blue-500"
        >
          <BpmValue row={row} />
        </button>
        <button
          type="button"
          title="BPM handmatig aanpassen"
          data-testid={`bpm-edit-${row.listing_id}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="mt-0.5 rounded-sm border border-slate-200 p-0.5 text-slate-400 transition-colors duration-150 hover:border-blue-400 hover:text-blue-600"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    </td>
  );
};

// Inhoud van de NL-waarde-knop met early return i.p.v. geneste ternary.
const ValueContent = ({ row }) => {
  const h = row.headline || {};
  if (h.expected_market_value === null || h.expected_market_value === undefined) {
    return <span className="text-[11px] text-red-700">Onvoldoende vergelijkingsauto's</span>;
  }
  const n = row.valuation?.n_used ?? 0;
  if (row.valuation?.value_source === "MANUAL") {
    return (
      <>
        {eur(h.expected_market_value)}
        <div
          className="text-[9.5px] font-bold uppercase tracking-wide text-blue-700"
          data-testid={`value-manual-${row.listing_id}`}
        >
          Handmatig
        </div>
      </>
    );
  }
  const indicative = h.valuation_indicative || h.valuation_status === "INDICATIE";
  return (
    <>
      {eur(h.expected_market_value)}
      <div
        className={`text-[9.5px] uppercase tracking-wide ${
          indicative ? "font-bold text-red-600" : "text-slate-500"
        }`}
        data-testid={indicative ? `value-indicative-${row.listing_id}` : undefined}
      >
        {indicative ? `Indicatie · ${n} vergelijker${n === 1 ? "" : "s"}` : `${n} vergelijkers`}
      </div>
    </>
  );
};

export const ValueCell = ({ row, onExplain, onEdit }) => {
  const h = row.headline || {};
  const title =
    row.valuation?.value_source === "MANUAL"
      ? `Automatisch: ${eur(row.valuation?.automatic_expected_market_value, {
          fallback: "niet te bepalen",
        })}\nHandmatig: ${eur(h.expected_market_value)}`
      : `${row.valuation?.n_used ?? 0} NL vergelijkingsauto's`;
  return (
    <td className="num font-semibold" data-testid={`value-cell-${row.listing_id}`} title={title}>
      <div className="flex items-start justify-end gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExplain();
          }}
          className="text-right underline decoration-slate-300 decoration-dotted underline-offset-2 transition-colors duration-150 hover:decoration-blue-500"
        >
          <ValueContent row={row} />
        </button>
        <button
          type="button"
          title="Verwacht NL onderbouwen of handmatig aanpassen"
          data-testid={`value-edit-${row.listing_id}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="mt-0.5 rounded-sm border border-slate-200 p-0.5 text-slate-400 transition-colors duration-150 hover:border-blue-400 hover:text-blue-600"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    </td>
  );
};
